/* Pack-size buttons (1 bottle / 6 pack / 12 case) used to be three separate
 * Shopify variants, each with its own tracked inventory — so a 6-pack could
 * show in stock while the bottles themselves were sold out, or vice versa,
 * and stocking a pack meant remembering to keep a second (or third) SKU's
 * inventory in sync with the first by hand.
 *
 * This makes every pack size a quantity multiplier on the single-bottle
 * variant instead: choosing "6 pack" and clicking Add to cart adds 6 of the
 * bottle SKU, not 1 of a "NON1 6-Pack" SKU. One variant, one stock count,
 * and the pack buttons' sold-out state now follows it. The look is untouched
 * — same three buttons, same pack pricing shown on each — only what actually
 * gets added, and when they gray out, has changed.
 *
 * ProductForm (Alpine, compiled into Baseline's island-product.bundle.js —
 * no source available) owns the form's normal submit handling and the
 * radio inputs' availability styling. Rather than reach into that bundle,
 * this listens alongside it: a capture-phase submit listener pre-empts
 * ProductForm's own handler only when a multi-bottle pack is selected (a
 * single bottle still goes through the normal Alpine flow untouched), and
 * does its own /cart/add.js call.
 *
 * The cart drawer doesn't open or refresh on its own just because an item
 * landed in the cart — ModalCart, CartItems, CartFooter and the header
 * count all independently listen on document.body for one specific event,
 * "baseline:cart:afteradditem" (found in the compiled bundle; nothing
 * listens for a generic "cart updated" event, which is why an early version
 * of this that dispatched its own cart:refresh added the right item but
 * left the drawer looking empty until the page was reloaded). Those
 * listeners read detail.response as the FLAT line-item object Shopify's
 * own /cart/add.js returns for a single, non-items-array POST — so the
 * request below is deliberately in that flat form, not {items: [...]}.
 *
 * The pack radios and the <form> aren't DOM-nested — snippets/product-form.liquid
 * renders variant_picker and buy_buttons as separate, sibling blocks, so
 * input.form is null. [data-product-root] (already the scoping root for every
 * other .non-pack-* rule in this theme) is the real shared ancestor.
 */
(function () {
  if (window.__nonPackQuantityInit) return;
  window.__nonPackQuantityInit = true;

  function packInputs(root) {
    return root.querySelectorAll("[data-non-pack-count]");
  }

  // The bottle's availability is a fixed fact baked into each input's data
  // attributes at render time — it doesn't depend on which option is
  // selected, so this only needs to run once per root (plus after any
  // change, in case Alpine's own pass re-touches these inputs' classes).
  function syncSoldOut(root) {
    packInputs(root).forEach(function (input) {
      var available = input.dataset.nonPackBottleAvailable === "true";
      input.classList.toggle("sold-out", !available);
    });
  }

  function syncAllRoots() {
    document.querySelectorAll("[data-product-root]").forEach(function (root) {
      if (packInputs(root).length) syncSoldOut(root);
    });
  }

  function selectRegionalPack(selector, input) {
    if (!selector || !input) return;
    selector.dataset.nonPackSelected = input.dataset.nonPackCount;
    selector.querySelectorAll("label, [data-non-pack-choice]").forEach(function (control) {
      var selected = control.htmlFor === input.id ||
        control.dataset.nonPackChoice === input.dataset.nonPackCount;
      control.classList.toggle("is-selected", selected);
      control.setAttribute("aria-pressed", selected ? "true" : "false");
    });
  }

  function initRegionalSelectors() {
    document.querySelectorAll("[data-non-pack-selector]").forEach(function (selector) {
      selector.querySelectorAll("[data-non-pack-choice]:not([data-non-pack-bound])").forEach(function (button) {
        button.dataset.nonPackBound = "true";
      function choosePack(event) {
        event.preventDefault();
        event.stopPropagation();
        var input = selector.querySelector(
          '[data-non-pack-count="' + button.dataset.nonPackChoice + '"]'
        );
        if (!input) return;
        input.checked = true;
        selectRegionalPack(selector, input);
      }
      button.addEventListener("pointerdown", choosePack);
      button.addEventListener("click", choosePack);
    });
    });
  }

  initRegionalSelectors();
  setTimeout(initRegionalSelectors, 500);

  // Initial pass after Alpine has mounted and done its own first pass, plus
  // on every option change (bubble phase runs after the input's own @change
  // listener, so this always sees Alpine's latest state before overriding it).
  setTimeout(syncAllRoots, 300);
  // The regional selector is a quantity control, not a Shopify variant
  // option. Baseline's delegated product-option handler otherwise sees its
  // radio change and restores the actual variant's first option, making the
  // visual selection snap straight back to "1 bottle". Keep this synthetic
  // control out of that handler; submission below still reads the checked
  // quantity and adds the correct number of bottle units.
  ["click", "input", "change"].forEach(function (eventName) {
    document.addEventListener(
      eventName,
      function (event) {
        var selector = event.target.closest("[data-non-pack-selector]");
        if (!selector) return;
        event.stopPropagation();
        var root = event.target.closest("[data-product-root]");
        if (eventName === "click") {
          var input = event.target.matches("input[data-non-pack-count]")
            ? event.target
            : selector.querySelector("#" + CSS.escape(event.target.closest("label")?.htmlFor || ""));
          if (input) {
            selectRegionalPack(selector, input);
            // Baseline can restore the true Shopify variant after the native
            // label click. Re-assert this independent quantity choice after
            // that synchronous render completes.
            setTimeout(function () {
              input.checked = true;
              selectRegionalPack(selector, input);
              if (root) syncSoldOut(root);
            }, 0);
          }
        }
        if (root && eventName !== "click") syncSoldOut(root);
      },
      true
    );
  });
  document.addEventListener("change", function (event) {
    if (!event.target.matches("[data-non-pack-count]")) return;
    var root = event.target.closest("[data-product-root]");
    if (root) syncSoldOut(root);
  });

  document.addEventListener(
    "submit",
    function (event) {
      var form = event.target;
      var root = form.closest("[data-product-root]");
      if (!root) return; // not a product form

      var regionalSelector = root.querySelector("[data-non-pack-selector][data-non-pack-selected]");
      var selectedCount = regionalSelector && regionalSelector.dataset.nonPackSelected;
      var checked = selectedCount
        ? regionalSelector.querySelector('[data-non-pack-count="' + selectedCount + '"]')
        : root.querySelector("[data-non-pack-count]:checked");
      if (!checked) return; // this product has no pack-size option — leave Alpine alone

      var available = checked.dataset.nonPackBottleAvailable === "true";
      if (!available) {
        // Belt and braces: the button should already read Sold out via
        // syncSoldOut(), but the pack SKU's own (irrelevant, now-unused)
        // availability is still what Alpine's disabled-state check reads,
        // so block the submit directly rather than trust that alone.
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      var count = parseInt(checked.dataset.nonPackCount, 10) || 1;
      if (count <= 1) return; // single bottle: normal Alpine add-to-cart flow

      event.preventDefault();
      event.stopImmediatePropagation();

      var bottleId = checked.dataset.nonPackBottleVariantId;
      var button = root.querySelector(".add-to-cart");
      if (button) button.disabled = true;

      fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ id: Number(bottleId), quantity: count }),
      })
        .then(function (response) {
          if (!response.ok) {
            return response.json().then(function (err) {
              throw new Error(err.description || "Could not add this to the cart.");
            });
          }
          return response.json();
        })
        .then(function (data) {
          document.body.dispatchEvent(
            new CustomEvent("baseline:cart:afteradditem", {
              bubbles: true,
              detail: { response: data, sourceId: form.id },
            })
          );
        })
        .catch(function (error) {
          window.alert(error.message);
        })
        .finally(function () {
          if (button) button.disabled = false;
        });
    },
    true
  );
})();
